/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

// This class allows integration with OCAPI Baskets Resource
// https://documentation.b2c.commercecloud.salesforce.com/DOC2/topic/com.demandware.dochelp/OCAPI/current/shop/Resources/Baskets.html
// This implementations coverts CAPI requests to OCAPI requests as there are fundamental differences between the APIS
// One major difference is OCAPI uses snake_case and CAPI uses camelCase for this reaso you will see a utility function in here that convert
// from camelCase to snake_case - camelCaseKeysToUnderscore
// createOcapiFetch is another utility function that returns the response from OCAPI in the fromat returned from CAPI
// Another utility function - checkRequiredParameters is used to check if the parameters or body objects necessary for a call are
// present in the request before making it

import { CommerceApiConfig } from '~/lib/data-contract/salesforce';
import { camelCaseKeysToUnderscore, checkRequiredParameters, createOcapiFetch } from './utils';

class OcapiShopperBaskets {
    private fetch: (
        endpoint: string,
        method: string,
        args: any,
        methodName: string,
        body?: any,
    ) => Promise<any>;
    constructor(config: CommerceApiConfig) {
        this.fetch = createOcapiFetch(config);
    }

    async createBasket(...args: any[]) {
        const { body } = args[0];
        return await this.fetch('baskets', 'POST', args, 'createBasket', body);
    }

    async updateBasket(...args: any[]) {
        const required = ['basketId', 'body'];
        const requiredParametersError = checkRequiredParameters(args[0], required);
        if (requiredParametersError) {
            return requiredParametersError;
        }
        const {
            parameters: { basketId },
            body,
        } = args[0];
        return this.fetch(`baskets/${basketId}`, 'PATCH', args, 'updateBasket', body);
    }

    async getBasket(...args: any[]) {
        const required = ['basketId'];
        const requiredParametersError = checkRequiredParameters(args[0], required);
        if (requiredParametersError) {
            return requiredParametersError;
        }
        const { basketId } = args[0].parameters;
        return await this.fetch(`baskets/${basketId}`, 'GET', args, 'getBasket');
    }

    async addItemToBasket(...args: any[]) {
        const required = ['basketId', 'body'];
        const requiredParametersError = checkRequiredParameters(args[0], required);
        if (requiredParametersError) {
            return requiredParametersError;
        }
        const {
            parameters: { basketId },
            body,
        } = args[0];
        return this.fetch(
            `baskets/${basketId}/items`,
            'POST',
            args,
            'addToBasket',
            camelCaseKeysToUnderscore(body),
        );
    }

    async updateItemInBasket(...args: any[]) {
        const required = ['basketId', 'body'];
        const requiredParametersError = checkRequiredParameters(args[0], required);
        if (requiredParametersError) {
            return requiredParametersError;
        }
        const {
            parameters: { basketId, itemId },
            body,
        } = args[0];
        return this.fetch(
            `baskets/${basketId}/items/${itemId}`,
            'PATCH',
            args,
            'updateItemInBasket',
            camelCaseKeysToUnderscore(body),
        );
    }

    async removeItemFromBasket(...args: any[]) {
        const required = ['basketId', 'itemId'];
        const requiredParametersError = checkRequiredParameters(args[0], required);
        if (requiredParametersError) {
            return requiredParametersError;
        }
        const { basketId, itemId } = args[0].parameters;
        return this.fetch(
            `baskets/${basketId}/items/${itemId}`,
            'DELETE',
            args,
            'removeItemFromBasket',
        );
    }

    async addPaymentInstrumentToBasket(...args: any[]) {
        const required = ['basketId', 'body'];
        const requiredParametersError = checkRequiredParameters(args[0], required);

        if (requiredParametersError) {
            return requiredParametersError;
        }

        const {
            parameters: { basketId },
            body,
        } = args[0];
        return this.fetch(
            `baskets/${basketId}/payment_instruments`,
            'POST',
            args,
            'addPaymentInstrumentToBasket',
            camelCaseKeysToUnderscore(body),
        );
    }

    async updatePaymentInstrumentInBasket(...args: any[]) {
        const required = ['basketId', 'paymentInstrumentId'];
        const requiredParametersError = checkRequiredParameters(args[0], required);

        if (requiredParametersError) {
            return requiredParametersError;
        }

        const {
            body,
            parameters: { basketId, paymentInstrumentId },
        } = args[0];

        return this.fetch(
            `baskets/${basketId}/payment_instruments/${paymentInstrumentId}`,
            'PATCH',
            args,
            'updatePaymentInstrumentInBasket',
            camelCaseKeysToUnderscore(body),
        );
    }

    // NOTE: While this is in Shopper Baskets, this should be in Shopper Orders.
    async updatePaymentInstrumentOfOrder(...args: any[]) {
        const required = ['orderNo', 'paymentInstrumentId', 'body'];
        const requiredParametersError = checkRequiredParameters(args[0], required);

        if (requiredParametersError) {
            return requiredParametersError;
        }

        const {
            parameters: { orderNo, paymentInstrumentId },
            body,
        } = args[0];

        return this.fetch(
            `orders/${orderNo}/payment_instruments/${paymentInstrumentId}`,
            'PATCH',
            args,
            'updatePaymentInstrumentOfOrder',
            body,
        );
    }

    async removePaymentInstrumentFromBasket(...args: any[]) {
        const required = ['basketId', 'paymentInstrumentId'];
        const requiredParametersError = checkRequiredParameters(args[0], required);
        if (requiredParametersError) {
            return requiredParametersError;
        }
        const {
            parameters: { basketId, paymentInstrumentId },
        } = args[0];
        return this.fetch(
            `baskets/${basketId}/payment_instruments/${paymentInstrumentId}`,
            'DELETE',
            args,
            'removePaymentInstrumentFromBasket',
        );
    }

    async getPaymentMethodsForBasket(...args: any[]) {
        const required = ['basketId'];
        const requiredParametersError = checkRequiredParameters(args[0], required);
        if (requiredParametersError) {
            return requiredParametersError;
        }
        const { basketId } = args[0].parameters;
        return this.fetch(
            `baskets/${basketId}/payment_methods`,
            'GET',
            args,
            'getPaymentMethodsForShipment',
        );
    }

    async getShippingMethodsForShipment(...args: any[]) {
        const required = ['basketId', 'shipmentId'];
        const requiredParametersError = checkRequiredParameters(args[0], required);
        if (requiredParametersError) {
            return requiredParametersError;
        }
        const { basketId, shipmentId } = args[0].parameters;
        return this.fetch(
            `baskets/${basketId}/shipments/${shipmentId}/shipping_methods`,
            'GET',
            args,
            'getShippingMethodsForShipment',
        );
    }

    async updateBillingAddressForBasket(...args: any[]) {
        const required = ['basketId', 'body'];
        const requiredParametersError = checkRequiredParameters(args[0], required);
        if (requiredParametersError) {
            return requiredParametersError;
        }
        const {
            parameters: { basketId, useAsShipping = false },
            body,
        } = args[0];
        return this.fetch(
            `baskets/${basketId}/billing_address?use_as_shipping=${useAsShipping}`,
            'PUT',
            args,
            'updateBillingAddressForBasket',
            camelCaseKeysToUnderscore(body),
        );
    }

    async updateShippingAddressForShipment(...args: any[]) {
        const required = ['basketId', 'body', 'shipmentId'];
        const requiredParametersError = checkRequiredParameters(args[0], required);
        if (requiredParametersError) {
            return requiredParametersError;
        }
        const {
            parameters: { basketId, shipmentId, useAsBilling = false },
            body,
        } = args[0];
        return this.fetch(
            `baskets/${basketId}/shipments/${shipmentId}/shipping_address?use_as_billing=${useAsBilling}`,
            'PUT',
            args,
            'updateShippingAddressForShipment',
            camelCaseKeysToUnderscore(body),
        );
    }

    async updateShippingMethodForShipment(...args: any[]) {
        const required = ['basketId', 'body', 'shipmentId'];
        const requiredParametersError = checkRequiredParameters(args[0], required);
        if (requiredParametersError) {
            return requiredParametersError;
        }

        const {
            parameters: { basketId, shipmentId },
            body: _body,
        } = args[0];

        const { storeId, deliveryDate, ...body } = _body;
        const searchParams = new URLSearchParams();

        if (storeId) {
            searchParams.append('pickupStoreId', storeId);
        }

        const params = searchParams.toString();
        const endpoint = `baskets/${basketId}/shipments/${shipmentId}/shipping_method`;
        const url = params ? `${endpoint}?${params}` : endpoint;

        return this.fetch(url, 'PUT', args, 'updateShippingMethodForShipment', {
            c_deliveryDate: deliveryDate,
            ...camelCaseKeysToUnderscore(body),
        });
    }

    async updateCustomerForBasket(...args: any[]) {
        const required = ['basketId', 'body'];
        const requiredParametersError = checkRequiredParameters(args[0], required);
        if (requiredParametersError) {
            return requiredParametersError;
        }
        const {
            parameters: { basketId },
            body,
        } = args[0];
        return this.fetch(
            `baskets/${basketId}/customer`,
            'PUT',
            args,
            'updateCustomerForBasket',
            camelCaseKeysToUnderscore(body),
        );
    }

    async deleteBasket(...args: any[]) {
        const required = ['basketId'];
        const requiredParametersError = checkRequiredParameters(args[0], required);
        if (requiredParametersError) {
            return requiredParametersError;
        }
        const { basketId } = args[0].parameters;
        return this.fetch(`baskets/${basketId}`, 'DELETE', args, 'deleteBasket');
    }

    async addCouponToBasket(...args: any[]) {
        const required = ['basketId', 'body'];
        const requiredParametersError = checkRequiredParameters(args[0], required);
        if (requiredParametersError) {
            return requiredParametersError;
        }
        const {
            parameters: { basketId },
            body,
        } = args[0];
        return this.fetch(
            `baskets/${basketId}/coupons`,
            'POST',
            args,
            'addCouponToBasket',
            camelCaseKeysToUnderscore(body),
        );
    }

    async removeCouponFromBasket(...args: any[]) {
        const required = ['basketId', 'couponItemId'];
        const requiredParametersError = checkRequiredParameters(args[0], required);
        if (requiredParametersError) {
            return requiredParametersError;
        }
        const {
            parameters: { basketId, couponItemId },
        } = args[0];
        return this.fetch(
            `baskets/${basketId}/coupons/${couponItemId}`,
            'DELETE',
            args,
            'removeCouponFromBasket',
        );
    }
}

export default OcapiShopperBaskets;
